/* ═══════════════════════════════════════════════════
   FIREBASE CONFIGURATION + HELPER FUNCTIONS
   
   File: src/utils/firebase.js
   
   This file:
   1. Initializes Firebase with your project config
   2. Exports auth (for login/signup)
   3. Exports db (for Firestore database)
   4. Provides helper functions to save/load data
═══════════════════════════════════════════════════ */

import { initializeApp } from "firebase/app";
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
} from "firebase/auth";
import {
  getFirestore,
  doc,
  setDoc,
  getDoc,
  collection,
  addDoc,
  query,
  orderBy,
  limit,
  getDocs,
  serverTimestamp,
} from "firebase/firestore";

/* ── 1. Firebase Config (reads from .env file) ── */
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

/* ── 2. Initialize Firebase ── */
const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);

/* ═══════════════════════════════════════════════════
   AUTHENTICATION HELPERS
═══════════════════════════════════════════════════ */

/**
 * Sign up a new user with email, password, and display name.
 * Also creates a user profile document in Firestore.
 */
export async function signUpUser(email, password, displayName) {
  // Create auth account
  const userCredential = await createUserWithEmailAndPassword(
    auth,
    email,
    password,
  );
  const user = userCredential.user;

  // Set display name on auth profile
  await updateProfile(user, { displayName });

  // Create user profile in Firestore
  await setDoc(doc(db, "users", user.uid), {
    displayName,
    email,
    createdAt: serverTimestamp(),
    lastLogin: serverTimestamp(),
    testsCompleted: 0,
    savedCareers: [],
  });

  return user;
}

/**
 * Log in an existing user.
 */
export async function loginUser(email, password) {
  const userCredential = await signInWithEmailAndPassword(
    auth,
    email,
    password,
  );

  // Update last login timestamp
  await setDoc(
    doc(db, "users", userCredential.user.uid),
    { lastLogin: serverTimestamp() },
    { merge: true },
  );

  return userCredential.user;
}

/**
 * Log out the current user.
 */
export async function logoutUser() {
  await signOut(auth);
}

/**
 * Listen to auth state changes (logged in / logged out).
 * Returns an unsubscribe function.
 */
export function onAuthChange(callback) {
  return onAuthStateChanged(auth, callback);
}

/* ═══════════════════════════════════════════════════
   FIRESTORE DATA HELPERS
═══════════════════════════════════════════════════ */

/**
 * Save career test results to Firestore.
 * Stores under: users/{uid}/testResults/{auto-id}
 *
 * @param {string} userId - The user's UID
 * @param {Object} results - { traits, careers, personality }
 */
export async function saveTestResults(userId, results) {
  if (!userId) return null;

  try {
    // Save to testResults subcollection
    const resultRef = await addDoc(
      collection(db, "users", userId, "testResults"),
      {
        traits: results.traits,
        personality: {
          title: results.personality.title,
          emoji: results.personality.emoji,
          dominant: results.personality.dominant,
          secondary: results.personality.secondary,
          stream: results.personality.stream,
        },
        topCareers: results.careers.slice(0, 5).map((c) => ({
          id: c.id,
          title: c.title,
          emoji: c.emoji,
          matchPercentage: c.matchPercentage,
        })),
        completedAt: serverTimestamp(),
      },
    );

    // Update user profile with latest results and test count
    await setDoc(
      doc(db, "users", userId),
      {
        latestPersonality: results.personality.title,
        latestPersonalityEmoji: results.personality.emoji,
        latestTraits: results.traits,
        testsCompleted: (await getUserProfile(userId))?.testsCompleted + 1 || 1,
        lastTestDate: serverTimestamp(),
      },
      { merge: true },
    );

    return resultRef.id;
  } catch (error) {
    console.error("Error saving test results:", error);
    return null;
  }
}

/**
 * Get user profile from Firestore.
 */
export async function getUserProfile(userId) {
  if (!userId) return null;

  try {
    const docSnap = await getDoc(doc(db, "users", userId));
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
  } catch (error) {
    console.error("Error getting user profile:", error);
    return null;
  }
}

/**
 * Get the user's test result history (most recent first).
 * @param {string} userId
 * @param {number} maxResults - How many to fetch (default 10)
 */
export async function getTestHistory(userId, maxResults = 10) {
  if (!userId) return [];

  try {
    const q = query(
      collection(db, "users", userId, "testResults"),
      orderBy("completedAt", "desc"),
      limit(maxResults),
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      // Convert Firestore timestamp to JS Date
      completedAt: doc.data().completedAt?.toDate?.() || new Date(),
    }));
  } catch (error) {
    console.error("Error getting test history:", error);
    return [];
  }
}

/**
 * Save/unsave a career to the user's favorites.
 */
export async function toggleSavedCareer(userId, careerId) {
  if (!userId) return;

  try {
    const profile = await getUserProfile(userId);
    const saved = profile?.savedCareers || [];

    const updated = saved.includes(careerId)
      ? saved.filter((id) => id !== careerId)
      : [...saved, careerId];

    await setDoc(
      doc(db, "users", userId),
      { savedCareers: updated },
      { merge: true },
    );

    return updated;
  } catch (error) {
    console.error("Error toggling saved career:", error);
    return null;
  }
}
